/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
/** @jsx jsx */

import moment from 'moment'
import { css, jsx } from '@emotion/react'

import {
  EuiFlexGroup,
  EuiFlexItem,
  EuiPanel,
  EuiSkeletonText,
  EuiSpacer,
  EuiText,
  useEuiBreakpoint,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { useGetCostsItemsV2 } from '@modules/billing-lib/hooks'
import CuiElasticConsumptionUnits from '@modules/cui/formatters/CuiElasticConsumptionUnits'

import IconTip from '../IconTip'

import type { FunctionComponent } from 'react'

interface Props {
  organizationId: string
}

const PanelWrapper = ({ children }) => (
  <EuiPanel
    data-test-id='month-to-date-panel'
    hasBorder={true}
    paddingSize='m'
    css={css`
      ${useEuiBreakpoint(['xs', 's', 'm'])} {
        width: 100%;
      }
      width: 30%;
    `}
  >
    {children}
  </EuiPanel>
)

const MonthToDateUsage: FunctionComponent<Props> = ({ organizationId }) => {
  const { data, isLoading, isError } = useGetCostsItemsV2({
    pathParameters: { organization_id: organizationId },
    queryParameters: {
      from: moment.utc().startOf('month').format(),
      to: moment.utc().endOf('day').format(),
    },
  })

  if (isLoading) {
    return (
      <PanelWrapper>
        <EuiSkeletonText lines={5} />
      </PanelWrapper>
    )
  }

  if (isError) {
    // silent error
    return null
  }

  return (
    <PanelWrapper>
      <EuiFlexGroup gutterSize='xs' alignItems='center' responsive={false}>
        <EuiFlexItem grow={false}>
          <EuiText size='s'>
            <h4>
              <FormattedMessage
                id='billing-usage.month-to-date.title'
                defaultMessage='Month-to-date usage'
              />
            </h4>
          </EuiText>
        </EuiFlexItem>

        <EuiFlexItem grow={false}>
          <IconTip
            content={
              <EuiText size='xs' color='subdued'>
                <FormattedMessage
                  id='billing-usage.month-to-date.tip'
                  defaultMessage="This amount reflects the total costs across all serverless projects and hosted
                deployments in your organization for the current month, up until today's date."
                />
              </EuiText>
            }
          />
        </EuiFlexItem>
      </EuiFlexGroup>
      <EuiFlexItem grow={false}>
        <EuiText size='xs'>
          <FormattedMessage
            id='billing-usage.month-to-date.subtitle'
            defaultMessage='for all serverless projects and hosted deployments'
          />
        </EuiText>
      </EuiFlexItem>
      <EuiSpacer size='m' />
      <EuiText size='m'>
        <h3>
          <CuiElasticConsumptionUnits
            dp={4}
            value={data?.total_ecu || 0}
            withSymbol={true}
            unit='none'
          />
        </h3>
      </EuiText>
      <EuiSpacer size='m' />
      <EuiText size='s' data-test-id='free-trial-info'>
        <FormattedMessage
          id='billing-usage.month-to-date.trial-info'
          defaultMessage='FREE during trial'
        />
      </EuiText>
    </PanelWrapper>
  )
}

export default MonthToDateUsage
